import React from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  List, ListItemButton, ListItemText, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

/**
 * ITEM-ACTIVE.2 — what to do about the stock, instead of a refusal.
 *
 * The guard was already right: an item with stock cannot be hidden, because
 * hiding it takes the quantity out of every list that would have shown it
 * while the number goes on being wrong underneath. What it said was
 * "409: this item still has stock on hand" — true, unactionable, and silent
 * about how much or what to do next.
 *
 * So this dialog does three things the refusal did not:
 *
 *   1. NAMES THE NUMBER. "3 on hand", not "has stock". A person cannot act on
 *      a rule, only on a quantity.
 *   2. SAYS WHY. Stock has to GO somewhere — be sold, wasted, moved or
 *      counted away. It cannot be deleted by hiding the thing that holds it.
 *   3. OPENS THE DOOR. Each route out is a link, prefilled with this item.
 *
 * WHAT IT DELIBERATELY DOES NOT DO: offer to zero the stock and deactivate in
 * one press. That shortcut is exactly the guardless delete this whole ticket
 * exists to undo — it would write off stock with no reason, no movement type
 * and no audit trail, from a dialog whose subject is housekeeping rather than
 * inventory. Every door leads to a flow that keeps its OWN confirmation and
 * its own reason field. Making the right thing easy is the job; making the
 * wrong thing one tap away is not.
 */

export interface DeactivationDoor {
  key: string;
  label: string;
  description: string;
  to: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  venueId: string;
  itemId: string;
  displayName: string;
  /** Net quantity in the way, from the server's 409. Negative is its own case. */
  quantity: number | null;
}

/**
 * The doors, per case.
 *
 * POSITIVE — real bottles exist and have to leave: sold and depleted already,
 * poured away, or moved to another location.
 *
 * NEGATIVE — nothing physical is in the way. The books say less than nothing,
 * which is a discrepancy, not stock. Offering "transfer" there would be
 * nonsense: you cannot move -2 bottles. The honest routes are to correct the
 * record or to count the shelf and let the count correct it.
 */
export const doorsFor = (
  venueId: string,
  itemId: string,
  quantity: number | null,
): DeactivationDoor[] => {
  const base = `/admin/venues/${venueId}`;
  const item = `itemId=${encodeURIComponent(itemId)}`;

  if (quantity != null && quantity < 0) {
    return [
      {
        key: 'adjustment',
        label: 'Correct with an adjustment',
        description: 'Record what really happened, with a reason. Best when you know the cause.',
        to: `${base}/inventory/adjust?${item}`,
      },
      {
        key: 'stock-count',
        label: 'Count the shelf',
        description: 'Count what is physically there and let the count set the record straight.',
        to: `${base}/stock-counts/create?${item}`,
      },
    ];
  }

  return [
    {
      key: 'wastage',
      label: 'Record wastage',
      description: 'Broken, spoiled or poured away. Records a reason against the loss.',
      to: `${base}/inventory/adjust?${item}&type=WASTE`,
    },
    {
      key: 'adjustment',
      label: 'Manual adjustment',
      description: 'Anything else that changed the count, with a note explaining it.',
      to: `${base}/inventory/adjust?${item}`,
    },
    {
      key: 'transfer',
      label: 'Transfer somewhere else',
      description: 'The stock is real and staying — it just belongs at another location.',
      to: `${base}/transfers/create?${item}`,
    },
  ];
};

const ItemDeactivationBlockedDialog: React.FC<Props> = ({
  open, onClose, venueId, itemId, displayName, quantity,
}) => {
  const navigate = useNavigate();
  const isNegative = quantity != null && quantity < 0;
  const doors = doorsFor(venueId, itemId, quantity);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {isNegative ? 'Records don’t balance' : 'There’s still stock to deal with'}
      </DialogTitle>

      <DialogContent>
        {/* The number first, and large. It is the whole reason this dialog is
            on screen, and it is what the user will act on. */}
        <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
          {quantity == null
            ? 'Stock on hand'
            : isNegative
              ? `${quantity} to resolve`
              : `${quantity} on hand`}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isNegative ? (
            <>
              <strong>{displayName}</strong> shows less than nothing on hand, which
              means the records disagree with the shelf. Making it inactive would
              hide the discrepancy rather than fix it — and hidden is exactly where
              a wrong number stops being fixable.
            </>
          ) : (
            <>
              <strong>{displayName}</strong> still has stock. Making it inactive
              would take that quantity out of every list that would have shown it,
              while the stock stays on the books. It has to go somewhere first.
            </>
          )}
        </Typography>

        <List dense sx={{ border: 1, borderColor: 'divider', borderRadius: 1, py: 0 }}>
          {doors.map((door, i) => (
            <ListItemButton
              key={door.key}
              divider={i < doors.length - 1}
              onClick={() => { onClose(); navigate(door.to); }}
            >
              <ListItemText primary={door.label} secondary={door.description} />
            </ListItemButton>
          ))}
        </List>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Once the count reaches zero, come back here and the item can be made
            inactive.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Leave it active
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemDeactivationBlockedDialog;
